import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown, TrendingUp } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

const LineReveal = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scaleBlob = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-end overflow-hidden pt-28 pb-16">
      {/* Soft spotlights */}
      <div className="pointer-events-none absolute -top-32 left-[10%] h-[560px] w-[560px] rounded-full bg-brand/15 blur-[130px]" />
      <div className="pointer-events-none absolute top-[30%] right-[5%] h-[420px] w-[420px] rounded-full bg-[#C9C79A]/30 blur-[120px]" />

      {/* Parallax organic visual */}
      <motion.div
        style={{ y: yBlob, scale: scaleBlob }}
        className="pointer-events-none absolute right-[-6%] top-[8%] w-[42vw] max-w-[640px] aspect-square"
      >
        <img
          src={IMAGES.heroTexture}
          alt=""
          className="h-full w-full object-cover rounded-[42%_58%_53%_47%/48%_46%_54%_52%] shadow-[0_40px_120px_-30px_rgba(110,108,70,0.5)] ring-1 ring-line"
        />
        <div className="absolute inset-0 rounded-[42%_58%_53%_47%/48%_46%_54%_52%] bg-brand/10 mix-blend-multiply" />
      </motion.div>

      {/* Floating metric card */}
      <motion.div
        style={{ y: yCard }}
        initial={{ opacity: 0, y: 40, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
        className="hidden lg:flex absolute right-[6%] bottom-[16%] z-20 items-center gap-4 bg-bg-2/80 backdrop-blur-xl border border-line rounded-2xl px-6 py-5 shadow-[0_20px_60px_-20px_rgba(27,28,18,0.25)]"
      >
        <div className="h-12 w-12 rounded-xl bg-brand/15 flex items-center justify-center text-brand">
          <TrendingUp className="h-6 w-6" />
        </div>
        <div>
          <div className="font-head font-bold text-ink text-lg leading-tight">{t.hero.floatCard}</div>
          <div className="text-sm text-ink-muted">{t.hero.floatCardSub}</div>
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-brand" />
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-brand">{t.hero.brandline}</span>
          </div>
          <p className="mt-3 text-sm text-ink-2 max-w-md">{t.hero.brandsub}</p>
        </motion.div>

        <motion.h1
          style={{ y: yText }}
          className="font-head font-black tracking-tighter text-ink leading-[0.92] text-[clamp(2.75rem,9vw,8.5rem)]"
          data-testid="hero-heading"
        >
          <LineReveal delay={0.35}>{t.hero.lines[0]}</LineReveal>
          <LineReveal delay={0.5}>
            <span className="text-brand">{t.hero.lines[1]}</span>
          </LineReveal>
          <LineReveal delay={0.65}>{t.hero.lines[2]}</LineReveal>
        </motion.h1>

        <div className="mt-10 grid lg:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            className="lg:col-span-6 text-base md:text-lg text-ink-2 leading-relaxed max-w-xl"
          >
            {t.hero.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1 }}
            className="lg:col-span-6 flex flex-col sm:flex-row lg:justify-end gap-4"
          >
            <button
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-medium rounded-full px-7 py-4 transition-colors duration-300 active:scale-95"
              data-testid="hero-cta-primary"
            >
              {t.hero.cta}
              <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </button>
            <button
              onClick={() => scrollTo("work")}
              className="inline-flex items-center justify-center gap-2 border border-line-hover hover:border-brand text-ink rounded-full px-7 py-4 transition-colors duration-300"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </div>

      <ArrowDown className="absolute bottom-8 left-1/2 -translate-x-1/2 h-6 w-6 text-brand animate-bounce hidden sm:block" />
    </section>
  );
}
